import React, { type FC, Suspense, useState } from 'react'
import { Button, Checkbox, Modal } from '@dostavkee/web-ui'

import { useDevtoolsOptions } from '../../model/hooks/use-devtools-options'
import styles from './devtools.module.scss'

const TanStackRouterDevtools = import.meta.env.PROD
    ? () => null
    : React.lazy(() =>
          import('@tanstack/react-router-devtools').then((module) => ({
              default: module.TanStackRouterDevtools,
          }))
      )

const TanStackReactQueryDevtools = import.meta.env.PROD
    ? () => null
    : React.lazy(() =>
          import('@tanstack/react-query-devtools').then((module) => ({
              default: module.ReactQueryDevtools,
          }))
      )

const INITIAL_OPTIONS = {
    isTanstackRouterEnabled: false,
    isTanstackQueryEnabled: false,
}

export const Devtools: FC = () => {
    const [isOpen, setIsOpen] = useState(false)

    const { options } = useDevtoolsOptions()

    return (
        <>
            <div
                className={styles['devtools']}
                data-testid='devtools-version'
                role='button'
                onClick={() => setIsOpen(true)}
            >
                <span>v: {VITE_APP_PACKAGE_VERSION}</span>
                <span>tag: {__GLOBAL__.VITE_APP_TAG_VERSION ?? 'local'}</span>
            </div>
            {options.isTanstackQueryEnabled && (
                <Suspense>
                    <TanStackReactQueryDevtools buttonPosition='bottom-right' position='bottom' />
                </Suspense>
            )}
            {options.isTanstackRouterEnabled && (
                <Suspense>
                    <TanStackRouterDevtools
                        position='bottom-right'
                        toggleButtonProps={{ style: { right: '72px', bottom: '18px' } }}
                    />
                </Suspense>
            )}

            {isOpen && <DevtoolsModal onClose={() => setIsOpen(false)} />}
        </>
    )
}

Devtools.displayName = 'Devtools'

const DevtoolsModal = ({ onClose }: { onClose: () => void }) => {
    const { options, setOptions } = useDevtoolsOptions()

    const [localOptions, setLocalOptions] = useState(options)

    const onSave = () => {
        setOptions(localOptions)
        onClose()
    }

    const onReset = () => {
        setOptions(INITIAL_OPTIONS)
        onClose()
    }

    return (
        <Modal isOpen className={styles['devtools__modal']} size='small' onClose={onClose}>
            <Modal.Header onClose={onClose}>Настройки разработчика</Modal.Header>
            <Modal.Body>
                <div className={styles['devtools__modal-body']}>
                    <Checkbox
                        checked={localOptions.isTanstackRouterEnabled}
                        data-testid='devtools-tanstack-router'
                        onChange={() =>
                            setLocalOptions({
                                ...localOptions,
                                isTanstackRouterEnabled: !localOptions.isTanstackRouterEnabled,
                            })
                        }
                    >
                        Devtools Tanstack-router
                    </Checkbox>
                    <Checkbox
                        checked={localOptions.isTanstackQueryEnabled}
                        data-testid='devtools-tanstack-query'
                        onChange={() =>
                            setLocalOptions({
                                ...localOptions,
                                isTanstackQueryEnabled: !localOptions.isTanstackQueryEnabled,
                            })
                        }
                    >
                        Devtools Tanstack-query
                    </Checkbox>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    data-testid='devtools-reset'
                    size='small'
                    variant='secondary'
                    onClick={onReset}
                >
                    Сбросить
                </Button>
                <Button data-testid='devtools-save' size='small' onClick={onSave}>
                    Сохранить
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
